import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { AdminLayout, inp } from "@/pages/admin/adminShared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Phone, Search, ShieldAlert, HeartPulse } from "lucide-react";

export default function MemberSafety() {
  const { user } = useAuth();
  const canAccess = user?.role === "admin" || user?.safety_access === true;
  const [q, setQ] = useState("");
  const [ecView, setEcView] = useState(null); // { member, data }

  const { data: members, isLoading } = useQuery({
    queryKey: ["safety-members"],
    queryFn: async () => (await api.get("/admin/safety/members")).data,
    enabled: canAccess,
  });

  const viewEmergency = async (m) => {
    try { const { data } = await api.get(`/admin/members/${m.id}/emergency`); setEcView({ member: m, data }); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!canAccess) {
    return (
      <AdminLayout title="Member Safety">
        <div className="rounded-2xl border border-border bg-card p-8 text-center" data-testid="safety-no-access">
          <ShieldAlert className="mx-auto mb-3 text-[#B04B26]" size={28} />
          <p className="text-sm text-[#5C4A42]">You do not have permission to view this area. Safety access must be granted by an administrator.</p>
        </div>
      </AdminLayout>
    );
  }

  const filtered = (members || []).filter((m) => m.name?.toLowerCase().includes(q.toLowerCase()));

  return (
    <AdminLayout title="Member Safety" subtitle="Restricted area for authorised staff. Every emergency-contact view is logged. No medical information is collected or shown here.">
      <div className="mb-4 flex items-center gap-2 rounded-2xl border border-[#F3C9A8] bg-[#FEF6EC] px-4 py-3 text-sm text-[#9A5B1E]" data-testid="safety-banner">
        <HeartPulse size={18} /> In a genuine emergency, search for the member and open their emergency contact. Access is audited.
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#6E5B51]" size={16} />
        <input className={`${inp} pl-9`} placeholder="Search members by name…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="safety-search" />
      </div>

      {isLoading ? <Spinner /> : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-[#6E5B51]"><tr>
              <th className="p-3">Name</th><th className="p-3">Role</th><th className="p-3">Emergency contact</th>
            </tr></thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-b border-border last:border-0" data-testid={`safety-row-${m.id}`}>
                  <td className="p-3 font-semibold text-[#2D1E18]">{m.name}</td>
                  <td className="p-3 text-[#5C4A42] capitalize">{m.role}</td>
                  <td className="p-3">
                    {m.has_emergency ? (
                      <button onClick={() => viewEmergency(m)} data-testid={`safety-view-${m.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#C85A32] px-3 py-1 text-xs font-bold text-white hover:bg-[#B04B26]">
                        <Phone size={13} /> View contact
                      </button>
                    ) : <span className="text-xs text-[#C9BDB4]">None on file</span>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-sm text-[#6E5B51]">No members found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!ecView} onOpenChange={(o) => !o && setEcView(null)}>
        <DialogContent data-testid="safety-emergency-dialog">
          <DialogHeader><DialogTitle>Emergency contact — {ecView?.member?.name}</DialogTitle></DialogHeader>
          {ecView?.data?.name || ecView?.data?.phone_display ? (
            <div className="space-y-2 text-sm">
              <p><span className="font-semibold text-[#2D1E18]">Name:</span> {ecView.data.name || "—"}</p>
              <p><span className="font-semibold text-[#2D1E18]">Relationship:</span> {ecView.data.relationship || "—"}</p>
              <p><span className="font-semibold text-[#2D1E18]">Phone:</span>{" "}
                {ecView.data.phone_display ? <a href={`tel:${ecView.data.phone || ecView.data.phone_display}`} className="font-bold text-[#B04B26] hover:underline">{ecView.data.phone_display}</a> : "—"}</p>
              <p className="pt-2 text-xs text-[#6E5B51]">This access has been logged.</p>
            </div>
          ) : <p className="text-sm text-[#6E5B51]">This member hasn't added an emergency contact.</p>}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
